import { useAuth } from 'context/auth';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PATH from 'crono-fe-common/constants/path';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { getLabelForIntegrationType } from 'crono-fe-common/utils/crm-utils';
import { FeConstants } from 'constants/FeConstants';
import { buildSalesforceLoginUrl } from 'hooks/services/salesforce/salesforcePkce';
import {
  ConnectWrapper,
  LayoutWrapper,
  OnboardingImageContainer,
} from './styles';
import Logo from 'assets/images/logo_crono.png';
import SalesforceLogo from 'assets/images/Icon_Salesforce.svg';
import HubspotLogo from 'assets/images/Icon_Hubspot.svg';
import PipedriveLogo from 'assets/images/Icon_Pipedrive.svg';
import ChartMogulLogo from 'assets/images/Icon_Chartmogul.svg';
import AttioLogo from 'assets/images/Icon_Attio.svg';
import ZohoLogo from 'assets/images/Icon_Zoho.svg';
import DynamicsLogo from 'assets/images/Icon_Dynamics.svg';
import { getZohoConnectUrl } from 'hooks/services/zoho/zohoOauthState';
import useDynamicsConnectFlow from 'pages/settings/Integrations/crmIntegrations/dynamics/useDynamicsConnectFlow';
import DynamicsEnvironmentPicker from 'pages/settings/Integrations/crmIntegrations/dynamics/DynamicsEnvironmentPicker';
import CrmConnectErrorBanner from 'pages/settings/Integrations/crmIntegrations/CrmConnectErrorBanner';
import InstallLinkedinExtensionModal from 'crono-fe-common/components/Modal/InstallLinkedinExtensionModal';
import useCRMIntegrationGuard from 'hooks/guards/useCRMIntegrationGuard';
import { useConditionalSnackBar } from 'context/snackbar';
import { Typography } from '@mui/material';
import { colors } from 'crono-fe-common/theme';
import CheckMarkIcon from 'crono-fe-common/icons/Icon-CheckMark';
import { MainButton } from 'crono-fe-common/components/CronoButton';
import { User } from 'crono-fe-common/types/user';
import { getExternalImage } from 'utils/uiHelpers';
import { ImageResources } from 'crono-fe-common/types/enums/imageResources';
import LogoutButton from 'components/LogoutButton';
import { ChartMogulModal } from 'pages/settings/Integrations/crmIntegrations/connectChartMogulModal';

const Connect: FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  useState<boolean>(false);
  const [openInstallLinkedinExtension, setOpenInstallLinkedinExtension] =
    useState<boolean>(false);

  const {
    isSuccessSF,
    isErrorSF,
    isSuccessHS,
    isErrorHS,
    isSuccessPD,
    isErrorPD,
    isSuccessCM,
    isErrorCM,
    isSuccessAT,
    isErrorAT,
    isSuccessZH,
    isErrorZH,
    isSuccessDY,
    isErrorDY,
    isSuccess,
    isLoading: isLoadingCRM,
    refetch: refetchCRMIntegrationGuard,
  } = useCRMIntegrationGuard();

  useEffect(() => {
    let t: NodeJS.Timeout;
    if (!isLoadingCRM && !isSuccess) {
      t = setTimeout(() => {
        refetchCRMIntegrationGuard();
      }, 1000);
    }
    return () => {
      clearTimeout(t);
    };
  }, [isLoadingCRM, isSuccess]);

  useEffect(() => {
    if (isSuccess) {
      refreshUser();
      navigate(PATH.HOME);
    }
  }, [isSuccess, user]);

  const salesforceLogin = async () => {
    if (!isSuccessSF) {
      try {
        window.location.replace(await buildSalesforceLoginUrl());
      } catch {
        showError('Error while connecting with Salesforce');
      }
    }
  };

  const hubspotLogin = () => {
    if (!isSuccessHS) {
      window.location.replace(
        FeConstants.HUBSPOT_LOGIN_URL(user?.company?.hubspotClientId),
      );
    }
  };

  const pipedriveLogin = () => {
    if (!isSuccessPD) {
      window.location.replace(FeConstants.PIPEDRIVE_LOGIN_URL);
    }
  };

  const attioLogin = () => {
    if (!isSuccessAT) {
      window.location.replace(FeConstants.ATTIO_LOGIN_URL);
    }
  };

  const zohoLogin = () => {
    if (!isSuccessZH) {
      window.location.replace(getZohoConnectUrl());
    }
  };

  const dynamicsConnect = useDynamicsConnectFlow();

  const dynamicsLogin = () => {
    if (!isSuccessDY) {
      dynamicsConnect.startConnect();
    }
  };

  const [error, setError] = useState<string | null>(null);

  const showError = (error: string) => {
    setError(error);
    setTimeout(() => {
      setError(null);
    }, 1000);
  };

  //To show the error coming from CRM, set by the handling
  useEffect(() => {
    const localStorageName = process.env.VITE_APP_ERROR_ONBOARDING_CALL;

    const interval = setInterval(() => {
      if (!localStorageName) return;
      const error = localStorage.getItem(localStorageName);
      if (!error) return;
      const parsedError: {
        error: string;
        crm: IntegrationType;
      } = JSON.parse(error);
      localStorage.removeItem(localStorageName);
      console.error(parsedError.error);
      showError(parsedError.error);
      clearInterval(interval);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    //If we are standalone, we should go to the home page
    if (user && user.integrationType === IntegrationType.CRONO) {
      navigate(PATH.HOME);
    }
  }, [user]);

  const [connectChartMogulModalOpened, setConnectChartMogulModalOpened] =
    useState(false);

  useConditionalSnackBar([
    {
      condition: !!error,
      message: error ?? 'Error while connecting with the CRM',
      severity: 'error',
    },
  ]);

  return (
    <LayoutWrapper id="layout-wrapper">
      {dynamicsConnect.environmentsToPick && (
        <DynamicsEnvironmentPicker
          environments={dynamicsConnect.environmentsToPick}
          onCancel={dynamicsConnect.cancelPicking}
          onConnect={dynamicsConnect.connectEnvironment}
        />
      )}
      {connectChartMogulModalOpened && (
        <ChartMogulModal
          close={() => {
            setConnectChartMogulModalOpened(false);
            refetchCRMIntegrationGuard();
          }}
        />
      )}
      <ConnectWrapper
        id="connect-wrapper"
        style={{
          height: '100%',
        }}
      >
        <div className="onboarding-steps-container">
          <div className="logo-container">
            <img src={Logo} className="logo" alt="Crono Logo" />
            <div className={'flex ml-auto absolute right-16'}>
              <LogoutButton />
            </div>
          </div>
          <div className="divider" />
          <div className="connect-crm">
            <div className="step-title-container">
              <Typography
                fontSize={'28px'}
                fontWeight={700}
                lineHeight={'32px'}
              >
                Connect with your CRM
              </Typography>
              <Typography
                fontSize={'16px'}
                fontWeight={500}
                lineHeight={'24px'}
                color={colors.grey11}
                textAlign={'center'}
                width={'300px'}
              >
                Integrate your usual CRM to transfer all prospects in seconds
              </Typography>
            </div>
            <div className="crm-connect-card">
              <InstallLinkedinExtensionModal
                open={openInstallLinkedinExtension}
                setOpen={setOpenInstallLinkedinExtension}
              />

              <div className="connect-card">
                <div className="providers">
                  {user &&
                    user?.company?.integrationType ===
                      IntegrationType.SALESFORCE && (
                      <ConnectCRMCard
                        user={user}
                        logo={SalesforceLogo}
                        loading={isLoadingCRM}
                        success={isSuccessSF}
                        error={isErrorSF}
                        loginFn={salesforceLogin}
                      />
                    )}
                  {user &&
                    user?.company?.integrationType ===
                      IntegrationType.HUBSPOT && (
                      <ConnectCRMCard
                        user={user}
                        logo={HubspotLogo}
                        loading={isLoadingCRM}
                        success={isSuccessHS}
                        error={isErrorHS}
                        loginFn={hubspotLogin}
                      />
                    )}
                  {user &&
                    user?.company?.integrationType ===
                      IntegrationType.PIPEDRIVE && (
                      <ConnectCRMCard
                        user={user}
                        logo={PipedriveLogo}
                        loading={isLoadingCRM}
                        success={isSuccessPD}
                        error={isErrorPD}
                        loginFn={pipedriveLogin}
                      />
                    )}

                  {user &&
                    user?.company?.integrationType ===
                      IntegrationType.CHART_MOGUL && (
                      <ConnectCRMCard
                        user={user}
                        logo={ChartMogulLogo}
                        loading={isLoadingCRM}
                        success={isSuccessCM}
                        error={isErrorCM}
                        loginFn={() => setConnectChartMogulModalOpened(true)}
                      />
                    )}

                  {user &&
                    user?.company?.integrationType ===
                      IntegrationType.ATTIO && (
                      <ConnectCRMCard
                        user={user}
                        logo={AttioLogo}
                        loading={isLoadingCRM}
                        success={isSuccessAT}
                        error={isErrorAT}
                        loginFn={attioLogin}
                      />
                    )}

                  {user &&
                    user?.company?.integrationType === IntegrationType.ZOHO && (
                      <ConnectCRMCard
                        user={user}
                        logo={ZohoLogo}
                        loading={isLoadingCRM}
                        success={isSuccessZH}
                        error={isErrorZH}
                        loginFn={zohoLogin}
                      />
                    )}

                  {user &&
                    user?.company?.integrationType ===
                      IntegrationType.DYNAMICS && (
                      <ConnectCRMCard
                        user={user}
                        logo={DynamicsLogo}
                        loading={isLoadingCRM}
                        success={isSuccessDY}
                        error={isErrorDY}
                        loginFn={dynamicsLogin}
                        errorMessage={dynamicsConnect.connectError}
                      />
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ConnectWrapper>
      <OnboardingImageContainer>
        <img
          src={getExternalImage(ImageResources.ONBOARDING)}
          alt="onboarding"
          className="onboarding-image"
        />
      </OnboardingImageContainer>
    </LayoutWrapper>
  );
};

interface IConnectCRMCardProps {
  user: User;
  logo: string;
  loading?: boolean;
  success?: boolean;
  error: boolean;
  loginFn: () => void;
  /** Why the last connect attempt failed, pinned above the card. */
  errorMessage?: string | null;
}
const ConnectCRMCard = ({
  user,
  logo,
  loading,
  success,
  error,
  loginFn,
  errorMessage,
}: IConnectCRMCardProps) => {
  const navigate = useNavigate();
  const integrationType = user.company?.integrationType;
  const integrationLabel = getLabelForIntegrationType(integrationType);

  const goHome = () => {
    navigate(`${PATH.HOME}`);
  };

  return (
    <>
      <div className="flex flex-col">
        {errorMessage && <CrmConnectErrorBanner message={errorMessage} />}
        <div
          className="provider-box"
          style={
            errorMessage
              ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 }
              : undefined
          }
        >
          <div className="icon-logo-container">
            <img
              src={logo}
              alt={integrationLabel}
              className={`connection-image ${integrationType}`}
            />
          </div>
          <div className={`connection-label`}>
            <Typography
              fontSize={'16px'}
              fontWeight={600}
              lineHeight={'24px'}
              color={colors.black}
            >
              {integrationLabel}
            </Typography>
            <Typography
              fontSize={'14px'}
              fontWeight={500}
              lineHeight={'18px'}
              color={
                !loading && success ? colors.darkGreen : colors.inactiveHover
              }
            >
              {success && !loading && 'Connected'}
              {(error || loading) && 'Not Connected'}
            </Typography>
          </div>
          <div
            className={`icons-buttons-container ${
              !loading && success ? 'connected' : 'not-connected'
            }`}
          >
            {!loading && success ? (
              <div className="connection-status">
                <Typography fontWeight={500}>Connected</Typography>
                <CheckMarkIcon color={colors.callScheduled} />
              </div>
            ) : (
              <div
                className="connection-status pointer"
                onClick={() => loginFn()}
              >
                <Typography fontWeight={500}>Connect</Typography>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <MainButton
          style={{
            minWidth: '120px',
          }}
          disabled={loading || !success}
          onClick={goHome}
        >
          Continue
        </MainButton>
      </div>
    </>
  );
};

export default Connect;
